// FirestoreUserHelper.ts
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "./Firebase";
import { getDefaultUserProgress } from "./UserProgressHelper";
import { getUserExperimentId } from "./ExpirmentHelper";

/**
 * Fetch complete user data from Firestore including progress tracking
 * If user document doesn't exist or is incomplete, creates default structure
 */
export async function fetchUserData(userId: string): Promise<any> {
    try {
        const userDocRef = doc(firestore, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            // User document doesn't exist - this shouldn't happen for logged-in users
            // but let's handle it gracefully
            console.warn(`❌ User document doesn't exist for userId: ${userId}`);
            return null;
        }

        const userData = userDoc.data();

        // Ensure all required progress tracking fields exist
        const defaultProgress = getDefaultUserProgress(userId, userData.experimentId || getUserExperimentId());
        
        // Merge existing data with defaults to fill in any missing fields
        const completeUserData = {
            // Default structure
            ...defaultProgress,
            // Overlay with existing user data
            ...userData,
            // Ensure progress arrays exist and are properly structured
            courses: mergeProgressArray(userData.courses, defaultProgress.courses),
            interviews: mergeProgressArray(userData.interviews, defaultProgress.interviews),
            challenges: mergeProgressArray(userData.challenges, defaultProgress.challenges),
            resources: mergeProgressArray(userData.resources, defaultProgress.resources),
            tools: mergeProgressArray(userData.tools, defaultProgress.tools),
            actions: mergeProgressArray(userData.actions, defaultProgress.actions),
            // Ensure essential flags exist
            hasOnboarded: userData.hasOnboarded ?? false,
            isPremium: userData.isPremium ?? false
        };

        return completeUserData;
    } catch (error) {
        console.error("❌ Error fetching user data from Firestore:", error);
        return null;
    }
}

/**
 * Merge existing progress array with default structure
 * Preserves existing progress while adding missing items
 */
function mergeProgressArray(existingArray: any[], defaultArray: any[]): any[] {
    if (!existingArray || !Array.isArray(existingArray)) {
        return defaultArray;
    }

    // Create a map of existing items by ID for quick lookup
    const existingMap = new Map();
    existingArray.forEach(item => {
        if (item && item.id) {
            existingMap.set(item.id, item);
        }
    });

    // Merge with defaults, preserving existing data
    return defaultArray.map(defaultItem => {
        const existing = existingMap.get(defaultItem.id);
        if (!existing) {
            return defaultItem; // Use default for missing items
        }

        // Merge existing with defaults, but preserve the correct ID from defaults
        const merged = { 
            ...defaultItem, // Start with correct structure and ID
            ...existing,    // Overlay existing completion status
            id: defaultItem.id // Force correct ID from defaults
        };

        // Handle sub-arrays (lessons, challengeItems)
        if (defaultItem.lessons && Array.isArray(defaultItem.lessons)) {
            merged.lessons = mergeSubItemArray(existing.lessons, defaultItem.lessons);
        }
        if (defaultItem.challengeItems && Array.isArray(defaultItem.challengeItems)) {
            merged.challengeItems = mergeSubItemArray(existing.challengeItems, defaultItem.challengeItems);
        }

        return merged;
    });
}

/**
 * Merge sub-item arrays (lessons, challengeItems)
 */
function mergeSubItemArray(existingSubArray: any[], defaultSubArray: any[]): any[] {
    if (!existingSubArray || !Array.isArray(existingSubArray)) {
        return defaultSubArray;
    }

    const existingSubMap = new Map();
    existingSubArray.forEach(item => {
        if (item && item.id !== undefined) {
            existingSubMap.set(item.id, item);
        }
    });

    return defaultSubArray.map(defaultSubItem => {
        const existing = existingSubMap.get(defaultSubItem.id);
        return existing ? { ...defaultSubItem, ...existing } : defaultSubItem;
    });
} 